import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { useOptionalAuth } from 'contexts/AuthContext';

import FileSelect from 'components/form/FileSelect';
import LabelMultiSelect from 'components/form/LabelMultiSelect';
import TextArea from 'components/form/TextArea';
import UserSelect from 'components/form/UserSelect';
import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import { buildPathLabel } from 'pages/workspace/components/treeUtils';
import {
  ArticleVisibility,
  createArticle,
  getArticle,
  updateArticle,
} from 'stores/ArticleStoreAdapter';
import { LabelResult, listLabel } from 'stores/LabelStoreAdapter';
import { WorkSpaceNode, getFileDetail, getWorkSpace, saveFile } from 'stores/WorkSpaceStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import { extractKnzFrontMatterMeta, syncKnzFrontMatter } from './knzFrontMatterSync';

const VISIBILITY_OPTIONS: ArticleVisibility[] = ['PUBLIC', 'INTERNAL', 'PRIVATE'];

/** md / knz ファイルのみ記事化可能 */
const ARTICLE_EXTENSIONS = ['md', 'knz'];

const getExtension = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return idx === -1 ? '' : name.slice(idx + 1).toLowerCase();
};

/** ノードの絶対パス（例: /docs/capital/article.knz）をツリーから組み立てる */
const buildNodePath = (parentId: string | null, name: string, nodes: WorkSpaceNode[]): string => {
  const dir = buildPathLabel(parentId, nodes); // ルート直下は "/"
  return dir === '/' ? `/${name}` : `${dir}/${name}`;
};

type FormState = {
  fileId: string;
  title: string;
  subtitle: string;
  description: string;
  authorUserIds: string[];
  labelIds: string[];
  visibility: ArticleVisibility;
};

const EMPTY_FORM: FormState = {
  fileId: '',
  title: '',
  subtitle: '',
  description: '',
  authorUserIds: [],
  labelIds: [],
  visibility: 'INTERNAL',
};

/**
 * 記事の新規登録 / 編集 兼用フォーム。ログイン必須。
 * - 新規: fileId をワークスペースから選択（?fileId= 指定時はプリセット）。knz は FrontMatter プレフィル。
 * - 編集: fileId は固定（変更不可）。更新は API6204。knz は加えて API6107 で FrontMatter 同期保存。
 */
const ArticleForm: React.FC = () => {
  const navigate = useNavigate();
  const { articleId } = useParams<{ articleId: string }>();
  const [searchParams] = useSearchParams();
  const presetFileId = searchParams.get('fileId') ?? '';
  const { userSession, users } = useOptionalAuth();

  const isEdit = Boolean(articleId);

  const [form, setForm] = React.useState<FormState>({ ...EMPTY_FORM, fileId: presetFileId });
  const [labels, setLabels] = React.useState<LabelResult[]>([]);
  /** ワークスペースの全ノード（ファイル選択モーダルのツリー表示用） */
  const [nodes, setNodes] = React.useState<WorkSpaceNode[]>([]);
  const [nodesLoading, setNodesLoading] = React.useState(false);
  /** 選択中ファイルの絶対パス（表示用）。空 かつ fileId ありのとき FileSelect が API で解決する */
  const [selectedFilePath, setSelectedFilePath] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  /** 選択中ファイルの拡張子（knz 判定に使用） */
  const [fileExtension, setFileExtension] = React.useState<string>('');

  // 未ログインならフォームは使わせない（ガードでも弾くが二重防御）
  React.useEffect(() => {
    if (userSession === null) {
      navigate('/login');
    }
  }, [userSession, navigate]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // 初期化: ラベル候補 + (新規:ワークスペースツリー / 編集:記事の現在値)
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const initLabels = listLabel()
      .then((res) => {
        if (!cancelled) setLabels(res);
      })
      .catch(() => {
        if (!cancelled) setLabels([]);
      });

    const initMain = (async () => {
      if (isEdit && articleId) {
        // 編集: 記事の現在値をロード
        const detail = await getArticle(articleId);
        if (cancelled) return;
        if (!detail.editable) {
          errorDefault('この記事を編集する権限がありません');
          navigate(`/devdocs/article/${articleId}`);
          return;
        }
        setForm({
          fileId: detail.fileId,
          title: detail.title,
          subtitle: detail.subtitle ?? '',
          description: detail.description ?? '',
          // 著者は userName しか返らないため userId へ逆引き（候補にある分のみ）
          authorUserIds: resolveAuthorUserIds(detail.authors.map((a) => a.userName)),
          labelIds: detail.labels.map((l) => l.labelId),
          visibility: detail.visibility,
        });
        setFileExtension(detail.extension);
      } else {
        // 新規: 自分のワークスペース全ノードを取得（ファイル選択モーダルのツリー用）
        setNodesLoading(true);
        const wsNodes = await getWorkSpace();
        if (cancelled) return;
        setNodes(wsNodes);
        setNodesLoading(false);
        // ?fileId= 指定時はプリセットしプレフィル
        if (presetFileId) {
          const target = wsNodes.find((n) => n.type === 'FILE' && n.fileId === presetFileId);
          if (target) {
            // ツリーから絶対パスを組み立てて表示（API 再取得不要）
            setSelectedFilePath(buildNodePath(target.parentId, target.name, wsNodes));
            await prefillFromFile(presetFileId, getExtension(target.name), cancelled);
          }
        }
      }
    })();

    Promise.all([initLabels, initMain])
      .catch((e) => {
        if (!cancelled) {
          errorDefault('初期化に失敗しました');
          // eslint-disable-next-line no-console
          console.error(e);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId, isEdit, presetFileId]);

  /** userName の配列から候補ユーザーの userId へ逆引き（重複名は最初の一致） */
  const resolveAuthorUserIds = (userNames: string[]): string[] => {
    return userNames
      .map((name) => users.find((u) => u.userName === name)?.userId)
      .filter((id): id is string => Boolean(id));
  };

  /** knz ファイルの FrontMatter からプレフィル */
  const prefillFromFile = async (fileId: string, ext: string, cancelled: boolean) => {
    setFileExtension(ext);
    if (ext !== 'knz') return;
    try {
      const detail = await getFileDetail(fileId);
      if (cancelled) return;
      const meta = extractKnzFrontMatterMeta(detail.detail);
      setForm((prev) => ({
        ...prev,
        fileId,
        title: meta.title || prev.title,
        subtitle: meta.subtitle || prev.subtitle,
        description: meta.description || prev.description,
        authorUserIds:
          meta.authorUserIds.length > 0
            ? meta.authorUserIds.filter((id) => users.some((u) => u.userId === id))
            : prev.authorUserIds,
        // FrontMatter の visible があれば公開設定にプレフィル
        visibility: meta.visibility ?? prev.visibility,
      }));
    } catch (e) {
      // プレフィル失敗は致命的でない
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  // 新規: ファイル選択で確定時（FileSelect が path も渡す）
  const handleSelectFile = async (file: {
    fileId: string;
    name: string;
    extension: string;
    path: string;
  }) => {
    update('fileId', file.fileId);
    setSelectedFilePath(file.path);
    setErrors((prev) => ({ ...prev, fileId: '' }));
    await prefillFromFile(file.fileId, file.extension, false);
  };

  // --- バリデーション（API仕様準拠。ValidationMessages 定数を使用） ---
  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.fileId) next.fileId = MSG_REQUIRED('本文ファイル');
    if (!form.title.trim()) next.title = MSG_REQUIRED('タイトル');
    else if (form.title.length > 128) next.title = MSG_MAX_LENGTH('タイトル', 128);
    if (form.subtitle.length > 1024) next.subtitle = MSG_MAX_LENGTH('サブタイトル', 1024);
    if (form.description.length > 1024) next.description = MSG_MAX_LENGTH('説明', 1024);
    if (form.authorUserIds.length === 0) next.author = MSG_REQUIRED('著者');
    if (form.labelIds.length === 0) next.labels = MSG_REQUIRED('ラベル');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      errorDefault('入力内容を確認してください');
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit && articleId) {
        // 記事メタ更新
        await updateArticle({
          articleId,
          title: form.title.trim(),
          subtitle: form.subtitle.trim() || undefined,
          description: form.description.trim() || undefined,
          authorUserIds: form.authorUserIds,
          labelIds: form.labelIds,
          visibility: form.visibility,
        });
        // knz の場合は FrontMatter を差し替えてファイル保存（API6107）
        if (fileExtension === 'knz') {
          await syncKnzFile();
        }
        successDefault('記事を更新しました');
        navigate(`/devdocs/article/${articleId}`);
      } else {
        const newId = await createArticle({
          fileId: form.fileId,
          title: form.title.trim(),
          subtitle: form.subtitle.trim() || undefined,
          description: form.description.trim() || undefined,
          authorUserIds: form.authorUserIds,
          labelIds: form.labelIds,
          visibility: form.visibility,
        });
        // knz の場合は公開直後に FrontMatter（title/subtitle/description/author/visible）を
        // フォーム値で同期して保存する（記事作成後に行うことで API6107 の記事同期対象にもなる）。
        if (fileExtension === 'knz') {
          await syncKnzFile();
        }
        successDefault('記事を公開しました');
        navigate(`/devdocs/article/${newId}`);
      }
    } catch (e) {
      errorDefault(
        isEdit ? '記事の更新に失敗しました' : '記事の公開に失敗しました',
        toErrorDetail(e),
      );
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * knz 記事の FrontMatter 同期保存（公開・更新の両方で使用）。
   * 現在の detail を取得し、FrontMatter の title / subtitle / description / author / visible を
   * フォーム値で差し替えて API6107 で保存する（本文コンテンツは変更しない）。
   */
  const syncKnzFile = async () => {
    const detail = await getFileDetail(form.fileId);
    const meta = {
      title: form.title.trim(),
      subtitle: form.subtitle.trim() || undefined,
      description: form.description.trim() || undefined,
      authorUserIds: form.authorUserIds,
      visibility: form.visibility,
    };
    const newSource = syncKnzFrontMatter(detail.detail, meta);
    await saveFile({
      fileId: form.fileId,
      detail: newSource,
      version: detail.version,
      // 記事同期はサーバ側でも行われるが、本文（FrontMatter）差し替えが主目的
      title: meta.title,
      subtitle: meta.subtitle,
      description: meta.description,
      visibility: meta.visibility,
      authorUserIds: meta.authorUserIds,
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  // ?fileId= で起動された場合はファイル固定（変更不可）
  const fileLocked = Boolean(presetFileId);

  return (
    <Stack spacing={2} sx={{ maxWidth: 720, mr: 'auto' }}>
      <Box>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            navigate(isEdit && articleId ? `/devdocs/article/${articleId}` : '/devdocs')
          }
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
          }}
        >
          戻る
        </Button>
      </Box>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          {isEdit ? '記事を編集' : '記事を公開'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          本文コンテンツはワークスペースのエディタで編集します。ここではメタ情報を設定します。
        </Typography>
        <Divider sx={{ mb: 2.5 }} />

        <Stack spacing={2.5}>
          {/* 本文ファイル（パス表示・fileId は内部保持）。
              編集 / ?fileId= 指定時は変更不可（locked）。path 未取得なら FileSelect が API 解決 */}
          <FileSelect
            label={fileLocked || isEdit ? '本文ファイル' : '本文ファイル（md / knz）'}
            required={!isEdit}
            fileId={form.fileId}
            path={selectedFilePath}
            nodes={nodes}
            nodesLoading={nodesLoading}
            selectableExtensions={ARTICLE_EXTENSIONS}
            onChange={handleSelectFile}
            error={!!errors.fileId}
            helperText={errors.fileId}
            locked={isEdit || fileLocked}
          />

          {/* タイトル */}
          <TextField
            label="タイトル"
            size="small"
            required
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            error={!!errors.title}
            helperText={errors.title}
            slotProps={{ htmlInput: { maxLength: 128 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          {/* サブタイトル */}
          <TextField
            label="サブタイトル"
            size="small"
            value={form.subtitle}
            onChange={(e) => update('subtitle', e.target.value)}
            error={!!errors.subtitle}
            helperText={errors.subtitle}
            slotProps={{ htmlInput: { maxLength: 1024 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          {/* 説明 */}
          <TextArea
            label="説明"
            value={form.description}
            onChange={(v) => update('description', v)}
            maxLength={1024}
            rows={4}
            errorMessage={errors.description}
          />

          {/* 著者 */}
          <UserSelect
            mode="multi"
            users={users}
            label="著者"
            required
            error={!!errors.author}
            helperText={errors.author}
            selectedUserIds={form.authorUserIds}
            onChange={(ids) => update('authorUserIds', ids)}
          />

          {/* ラベル */}
          <LabelMultiSelect
            labels={labels}
            selectedLabelIds={form.labelIds}
            onChange={(ids) => update('labelIds', ids)}
            onLabelCreated={(label) => setLabels((prev) => [...prev, label])}
            required
            error={!!errors.labels}
            helperText={errors.labels}
          />

          {/* 公開設定 */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              公開設定 *
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={form.visibility}
              onChange={(_, v: ArticleVisibility | null) => v && update('visibility', v)}
            >
              {VISIBILITY_OPTIONS.map((v) => (
                <ToggleButton
                  key={v}
                  value={v}
                  sx={{ textTransform: 'none', borderRadius: 2, px: 2 }}
                >
                  {ArticleVisibility[v]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Divider />

          <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
            <Button
              onClick={() =>
                navigate(isEdit && articleId ? `/devdocs/article/${articleId}` : '/devdocs')
              }
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {isEdit ? '更新' : '公開'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default ArticleForm;
